import { useCallback, useEffect, useRef, useState } from "react";
import { extractApiErrorCode, extractApiErrorMessage, isApiError } from "@akashnetwork/openapi-sdk";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";

import { useConfigureDeployTarget } from "@src/components/projects/DeployTargetGate/DeployTargetGate";
import { useServices } from "@src/context/ServicesProvider";
import { useMarkActivityOutcomes } from "@src/hooks/useHandledActivityOutcomes/useHandledActivityOutcomes";
import { type HeldActivityEvent, useHoldActivityEvents } from "@src/hooks/useHeldActivityEvents/useHeldActivityEvents";
import { useRecordOneClickDeploy } from "@src/hooks/useOneClickDeploys/useOneClickDeploys";
import { QueryKeys } from "@src/queries/queryKeys";
import { PENDING_ACTIVITY_POLL_MS, useLatestActivitiesQuery } from "@src/queries/useLatestActivitiesQuery";
import { BID_POLL_INTERVAL, useListBids } from "@src/queries/useListBids";
import { SKIP_REPORTING_REFUSED_INPUT } from "@src/services/query-error-policy/query-error-policy";
import { settingsIdAtom } from "@src/store/settingsStore";
import { PROJECT_NOT_FOUND_MESSAGE, PROJECT_REQUIRED_ERROR_CODE } from "@src/types/project";
import { formatBidId, parseBidId } from "@src/utils/bids/bidId";
import { ManifestYaml } from "@src/utils/deploymentData/helpers";
import { readProjectBudgetRefusal } from "@src/utils/projectBudgetRefusal";
import { importSimpleSdl } from "@src/utils/sdl/sdlImport";
import type { SdlSecretValues, UnresolvedSdlSecret } from "@src/utils/sdl/sdlSecrets";
import { unresolvedSecretMessage } from "@src/utils/sdl/sdlSecrets";
import type { ServicesPatch } from "@src/utils/sdl/sdlServicesPatch";
import { isEmptyServicesPatch, servicesPatchBetween } from "@src/utils/sdl/sdlServicesPatch";
import { sealSdlSecrets } from "@src/utils/sdl/sealSdlSecrets";
import { hasSdlReference } from "@src/utils/sdl/storedDefinition";
import { hardcodedTemplates } from "@src/utils/templates";
import { UrlService } from "@src/utils/urlUtils";
import { isWalletProvisioning, WALLET_PROVISIONING_ERROR_CODE, walletProvisioningRetry } from "@src/utils/walletProvisioning";
import { aggregateDeploymentResources } from "../DeploymentResourceSummary/deploymentResources";
import type { DepositShortfall } from "../depositShortfall/depositShortfall";
import { extractDepositShortfall, isBalanceTopUpPending } from "../depositShortfall/depositShortfall";
import { canLeaseElsewhere, deployErrorKindOf } from "./deployErrorKind";
import type { BidStrategy, DeploymentIntent } from "./deploymentIntent";

export type DeploymentFlowPhase = "configuring" | "creating" | "quoting" | "deploying" | "error";

/** A close still settling after a cancel; `failed` marks one verified still open, which the next create must close first. */
export type PendingClose = { dseq: string; failed: boolean; message?: string };

/** Which toast the form shows. A close failure reads differently from a failed quote request, and a refusal the user can pay their way out of needs an Add Funds action rather than an apology. */
export type FlowErrorKind = "create" | "close" | "no-providers" | "needs-funds" | "top-up-pending" | "no-match" | "inherited-unreadable" | "budget-exceeded";

export type FlowError = { message?: string; kind?: FlowErrorKind; shortfall?: DepositShortfall; projectId?: string };

export type AbandonedAttemptKind = Extract<FlowErrorKind, "no-match" | "no-providers">;

export type DeployError = { message?: string; canLeaseElsewhere?: boolean };

/** The live bids the flow polls while quoting (react-query-backed). Element shape derived from the shared `listBids` query. */
export type DeploymentBids = NonNullable<ReturnType<typeof useListBids>["data"]>["data"];

export interface DeploymentFlowState {
  phase: DeploymentFlowPhase;
  dseq: string | null;
  bidStrategy: BidStrategy;
  /** Provider chosen per placement, keyed by placement id; value is the offer's bid id (provider/dseq/gseq/oseq). Sibling state, not a form field. */
  selections: Record<string, string>;
  /**
   * Live bids for the current dseq while quoting, from the same react-query entry the flow polls. Empty otherwise.
   * Surfaced so the auto flow can match a provider off the flow's own query rather than re-declaring `listBids`.
   */
  bids: DeploymentBids;
  /** True once the lease is created, or once the api has taken it on in the background; the deploy overlay completes its progress before the page moves on. */
  deploySucceeded: boolean;
  /** True once the api has taken the lease on to finish in the background, so the page tells the user before they land on the deployments list. */
  isDeployedInBackground: boolean;
  /** Whether the deploy progress screen shows while deploying; held back while the api may still take the lease on in the background. */
  showsDeployProgress: boolean;
  /** True once a deploy the user confirmed has gone without a lease or an activity for longer than one takes, so they can retry or go back to the bids. */
  isDeployTakingLong: boolean;
  /** True once the wait for a first bid ran out with none; cleared when a bid lands or the deployment goes away. */
  noBidsReceived: boolean;
  deployError?: DeployError;
  error?: FlowError;
  /** The cancelled deployment closing in the background. Orthogonal to `phase`: the form stays editable throughout. */
  pendingClose: PendingClose | null;
  /** True once the api has accepted the create and finishes it in the background, so leaving the page no longer loses the deployment. */
  isCreatingInBackground: boolean;
  /** True from sending the lease request until it answers; the api finishes a lease it has been sent even once the page is gone. */
  isLeaseInFlight: boolean;
  /** The one-click deploy the api took on to create, match and lease by itself, which this page holds no dseq of and so can never close. */
  autoLeasedDseq: string | null;
  /** The bids the onboarding autopilot would have leased, one per placement, handed to the manual pick to start with them selected. */
  recommendedBidIds: string[];
}

export interface RequestQuotesOptions {
  name?: string;
  /** Typed secret values keyed by the name their SDL reference carries; sealed ahead of the create. */
  secrets?: SdlSecretValues;
  /** A deployment of the user's whose stored secret values the new one starts from. */
  inheritSecretsFrom?: string;
  /** What bid screening answered when the bids were requested, reported if no provider bids after all. */
  screening?: ScreeningSummary;
}

export interface ScreeningSummary {
  placementCount: number;
  /** Absent while any placement is still unscreened. */
  providerCount?: number;
  /** Of those, the ones flagged for not bidding on the requested gpu; absent like providerCount. */
  notBiddingProviderCount?: number;
}

export interface DeployOptions {
  /** Typed secret values keyed by the name their SDL reference carries; sealed into the pre-lease patch. */
  secrets?: SdlSecretValues;
  /** Secrets the SDL references that nothing holds a value for, which the api could not resolve once the manifest is sent. */
  unresolvedSecrets?: UnresolvedSdlSecret[];
  /** Set by the onboarding autopilot, so a failed deploy's analytics tell its failures apart from a provider the user picked. */
  isAutoDeploy?: boolean;
}

export interface DeploymentFlowActions {
  /** Creates the deployment from the given SDL. The caller passes the SDL generated from the just-submitted form
   * values so the request can never lag behind an in-flight edit. */
  requestQuotes: (sdl: string, options?: RequestQuotesOptions) => void;
  /** Sends the last create again as it went out, for a refusal the user has since cleared elsewhere, such as by adding funds. */
  requestQuotesAgain: () => void;
  cancelAndEdit: () => void;
  /** Ends the attempt in `error` and closes the deployment so its deposit is released; a human uses `cancelAndEdit`. */
  closeAndFail: (message: string, kind?: AbandonedAttemptKind) => void;
  /** Re-closes a deployment a background close left open. No-op while a close is in flight. */
  retryClose: () => void;
  /** Gives the attempt up for good before leaving the page: the phase stays put so nothing flashes while the page navigates away, and nothing leases it meanwhile. No-op while a lease is in flight, since that lease may still land. */
  discard: () => void;
  setBidStrategy: (strategy: BidStrategy, recommendedBidIds?: string[]) => void;
  refreshQuotes: () => void;
  retry: () => void;
  selectProvider: (placementId: string, bidId: string) => void;
  clearSelection: (placementId: string) => void;
  /** Creates the lease(s) and sends the manifest. The caller passes the current SDL so the manifest can be
   * rederived when it wasn't captured in this session (e.g. after a reload that resumed straight into quoting). */
  deploy: (sdl: string, options?: DeployOptions) => void;
}

export type DeploymentFlow = DeploymentFlowState & { actions: DeploymentFlowActions };

interface UseDeploymentFlowInput {
  intent: DeploymentIntent;
}

/** Default escrow deposit in USD (ACT maps 1:1 to USD). Matches `DEFAULT_DEPOSIT_USD` in the phased flow so a trial grant covers it. */
const DEFAULT_DEPOSIT = 0.5;

const BACKGROUND_QUERY: { async: "true" } = { async: "true" };

const HTTP_PAYMENT_REQUIRED = 402;

const HTTP_NOT_FOUND = 404;

/** A seal made to a key version the console no longer holds; a fresh context and a new seal is the remedy. */
const HTTP_CONFLICT = 409;

/** The api's code for a source deployment whose stored secrets can no longer be decrypted, which no retry can help. */
const INHERITED_SECRETS_UNREADABLE_CODE = "inherited_secrets_unreadable";

/** Hold after a successful lease so the deploy overlay's progress bar can fill to 100% and its final step turn green before redirecting. */
export const DEPLOY_SUCCESS_DWELL_MS = 1200;

/** Must stay above the second or so the api takes to hand a lease to the background, and below the 5 s or more it needs to create one during the request. */
const BACKGROUND_HANDOFF_WAIT_MS = 3000;

/** Retry is offered once this passes, so it must stay well above the 5 s or more the api needs to create a lease during the request. */
const SLOW_DEPLOY_TIMEOUT_MS = 30 * 1000;

/** How long a first bid may take before the user is told none came yet; the deployment stays open, since a late bid can still land. */
export const NO_BIDS_TIMEOUT_MS = 60 * 1000;

/** Error surfaced when a deployment draws no provider bid at all. */
export const NO_PROVIDERS_MESSAGE = "No providers are available for this deployment right now. Try adjusting your deployment and requesting new bids.";

/** An `active` bid is one this deployment already holds the lease on — exactly what a resumed selection points at. */
const LIVE_BID_STATES = new Set(["open", "active"]);

/** Surfaced when the SDL on screen can't be turned into a provider manifest at deploy time. */
const MANIFEST_BUILD_MESSAGE = "We couldn't prepare this deployment's manifest. Check your SDL and try again.";

/** Surfaced when deploy is reached with nothing selected — the manual CTA is disabled for that, an autopilot is not. */
const NO_SELECTION_MESSAGE = "No provider is selected for this deployment.";

/** Surfaced when the create-deployment retry budget is exhausted while the trial wallet is still provisioning server-side. */
const WALLET_PROVISIONING_TIMEOUT_MESSAGE =
  "Your account is still being set up. Please try again in a few minutes, or contact support if this keeps happening.";

/** A trial wallet still provisioning once the retries run out is Console's to answer for, unlike a refusal of the user's input. */
const SKIP_REPORTING_REFUSED_CREATE = {
  skipErrorReporting: (error: unknown) => !isWalletProvisioning(error) && SKIP_REPORTING_REFUSED_INPUT.skipErrorReporting(error)
};

export const DEPENDENCIES = {
  useServices,
  useListBids,
  useRouter,
  useQueryClient,
  useLatestActivitiesQuery,
  useConfigureDeployTarget,
  useMarkActivityOutcomes,
  useHoldActivityEvents,
  useRecordOneClickDeploy,
  // eslint-disable-next-line akash/dependencies-component-or-hook
  manifestFromSdl,
  // eslint-disable-next-line akash/dependencies-component-or-hook
  deploymentResourcesFromSdl,
  // eslint-disable-next-line akash/dependencies-component-or-hook
  sealSdlSecrets,
  // eslint-disable-next-line akash/dependencies-component-or-hook
  servicesPatchBetween
};

/**
 * Controlled lifecycle state machine for the configure flow. Owns interaction state (phase, dseq,
 * bidStrategy) and mirrors dseq/bid-strategy to the URL; server state (screened providers, bids)
 * lives in react-query via `usePlacementOffers`. Resumes in `quoting` when the URL already carries
 * a dseq, so a reload picks up live bids rather than restarting.
 */
export function useDeploymentFlow({ intent }: UseDeploymentFlowInput, dependencies: typeof DEPENDENCIES = DEPENDENCIES): DeploymentFlow {
  const { api, analyticsService } = dependencies.useServices();
  const router = dependencies.useRouter();
  const queryClient = dependencies.useQueryClient();
  const settingsId = useAtomValue(settingsIdAtom);
  /** Read by hook-level mutation callbacks, which still run once the page is gone and its own callbacks no longer do. */
  const discardedRef = useRef(false);
  /** Only the per-call callbacks mark an outcome handled, since they run only while this page is still there to show it. */
  const { forgetEarlierOutcomes, markOutcomeHandled, followUntilSettled, stopFollowing } = dependencies.useMarkActivityOutcomes();
  const holdActivityEvents = dependencies.useHoldActivityEvents();
  const recordOneClickDeploy = dependencies.useRecordOneClickDeploy();
  const deployTarget = dependencies.useConfigureDeployTarget();
  const deployTargetProjectId = deployTarget.project?.id;
  const forgetProject = deployTarget.forgetProject;
  const createDeployment = api.v1.createDeployment.useMutation({
    ...walletProvisioningRetry,
    meta: SKIP_REPORTING_REFUSED_CREATE,
    onSuccess: function closeWhatADiscardedCreateOpened(result) {
      if (discardedRef.current && !acceptedActivityIdOf(result.data)) closeDeployment.mutate(closeRequestFor(result.data.dseq, true));
    }
  });
  const closeDeployment = api.v1.closeDeployment.useMutation({
    onSettled: function refreshListsAfterADiscardedClose() {
      if (discardedRef.current) invalidateClosedDeploymentCaches();
    }
  });
  const createLease = api.v1.createLease.useMutation();
  const updateDeployment = api.v1.updateDeployment.useMutation();
  const patchDeployment = api.v1.patchDeployment.useMutation();
  const getDeployment = api.v1.getDeployment.useMutation();
  const getSdlSecretsContext = api.v1.getSDLSecretsContext.useMutation();

  const [phase, setPhase] = useState<DeploymentFlowPhase>(intent.dseq ? "quoting" : "configuring");
  const [dseq, setDseq] = useState<string | null>(intent.dseq ?? null);
  const [bidStrategy, setBidStrategyState] = useState<BidStrategy>(intent.bidStrategy);
  const [error, setError] = useState<FlowError | undefined>(undefined);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [manifest, setManifest] = useState<string | null>(null);
  /** The SDL the create sent, which a quoting-window edit is diffed against; null on a session that never saw the create. */
  const [createdSdl, setCreatedSdl] = useState<string | null>(null);
  const [deployError, setDeployError] = useState<DeployError | undefined>(undefined);
  const [deploySucceeded, setDeploySucceeded] = useState(false);
  const [isDeployedInBackground, setIsDeployedInBackground] = useState(false);
  const [showsDeployProgress, setShowsDeployProgress] = useState(false);
  /** The api answers a lease it takes on with a 202 and one it creates during the request with a 200, and the page can't tell which beforehand. */
  const [isAwaitingLeaseHandoff, setIsAwaitingLeaseHandoff] = useState(false);
  /** The deploy the user confirmed, timed until it gets a lease or an activity; an autopilot's deploy is not timed here. */
  const [watchedDeploy, setWatchedDeploy] = useState<{ attempt: DeployAttempt; hasTimedOut: boolean } | null>(null);
  const [isLeaseInFlight, setIsLeaseInFlight] = useState(false);
  const [pendingClose, setPendingClose] = useState<PendingClose | null>(null);
  const [noBidsReceived, setNoBidsReceived] = useState(false);
  const [autoLeasedDseq, setAutoLeasedDseq] = useState<string | null>(null);
  const [recommendedBidIds, setRecommendedBidIds] = useState<string[]>([]);

  /** A close the api took on in the background, followed through the activity feed until it settles; `token` is the close that started it. */
  const [backgroundClose, setBackgroundClose] = useState<{ dseq: string; token: number; activityId: string } | null>(null);
  const { data: activities } = dependencies.useLatestActivitiesQuery({ enabled: backgroundClose !== null });

  /** A create the api took on in the background, followed by its activity's id until it settles; `attempt` is the request that sent it. */
  const [backgroundCreate, setBackgroundCreate] = useState<{ dseq: string; activityId: string; attempt: number } | null>(null);

  /** Read on unmount: a create still unsettled when the page goes is the activity host's to announce. */
  const followedCreateIdRef = useRef<string | null>(null);
  followedCreateIdRef.current = backgroundCreate?.activityId ?? null;

  useEffect(
    function handTheFollowedCreateToTheHost() {
      return function stopFollowingTheCreate() {
        if (followedCreateIdRef.current) stopFollowing(followedCreateIdRef.current);
      };
    },
    [stopFollowing]
  );

  /** Read by id rather than from the feed, whose latest page a burst of other actions can push the create out of before it settles. */
  const { data: backgroundCreateActivity } = api.v1.getActivity.useQuery(
    { id: backgroundCreate?.activityId ?? "" },
    { enabled: backgroundCreate !== null, refetchInterval: PENDING_ACTIVITY_POLL_MS }
  );

  /** The deployment once the chain has it: one still being created in the background is not there to close yet. */
  const createdDseq = dseq === backgroundCreate?.dseq ? null : dseq;

  const intentRef = useRef(intent);
  intentRef.current = intent;

  /** Read in the async create-success callback so a create resolving after a strategy switch uses the current value. */
  const bidStrategyRef = useRef(bidStrategy);
  bidStrategyRef.current = bidStrategy;

  /**
   * Bumped on every requestQuotes and every cancel, so any create from a superseded attempt is treated as stale: one
   * not yet started (still behind a pre-create close) is skipped in `create()`, and one already in flight has its late
   * success auto-close the just-created deployment instead of resuming.
   */
  const createAttemptRef = useRef(0);

  /** A Retry can go out while the deploy it replaces is still running, so only the newest attempt may settle the flow. */
  const deployAttemptRef = useRef(0);

  /** The dseq of the close in flight. Read synchronously so a create can never overlap one and open a second deployment. */
  const closingDseqRef = useRef<string | null>(null);

  /** A second create sent while one is out would take over its callbacks, leaving whatever the first opens unclosed, so the next create waits for it. */
  const createInFlightRef = useRef(false);

  /** The create waiting on that close or create, run once the deployment is verified gone and dropped when it is not. */
  const queuedCreateRef = useRef<(() => void) | null>(null);

  /** Pins a close's outcome to the close that started it, so a superseded one can never settle a newer session. */
  const closeTokenRef = useRef(0);

  const lastQuoteRequestRef = useRef<{ sdl: string; options: RequestQuotesOptions } | null>(null);

  const bidsQuery = dependencies.useListBids(dseq, { enabled: phase === "quoting", refetchInterval: BID_POLL_INTERVAL });

  const redirectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(function clearRedirectTimerOnUnmount() {
    return function cancelPendingRedirect() {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  useEffect(
    function showProgressOfALeaseCreatedDuringTheRequest() {
      if (!isAwaitingLeaseHandoff) return;
      const timer = setTimeout(() => setShowsDeployProgress(true), BACKGROUND_HANDOFF_WAIT_MS);
      return function stopWaitingOnTheHandoff() {
        clearTimeout(timer);
      };
    },
    [isAwaitingLeaseHandoff]
  );

  useEffect(
    function reportADeployThatTakesTooLong() {
      if (!watchedDeploy || watchedDeploy.hasTimedOut || phase !== "deploying" || deploySucceeded) return;
      const timer = setTimeout(function timeOutWithoutALease() {
        if (discardedRef.current) return;
        const { event, properties } = deployFailedEventOf("lease_timeout", watchedDeploy.attempt);
        analyticsService.track(event, properties);
        setWatchedDeploy({ ...watchedDeploy, hasTimedOut: true });
      }, SLOW_DEPLOY_TIMEOUT_MS);
      return function stopTimingTheDeploy() {
        clearTimeout(timer);
      };
    },
    [watchedDeploy, phase, deploySucceeded, analyticsService]
  );

  useEffect(
    function pruneStaleSelections() {
      const bids = bidsQuery.data?.data;
      if (!bids || bids.length === 0) return;
      const liveBidIds = new Set(bids.filter(entry => LIVE_BID_STATES.has(entry.bid.state)).map(entry => formatBidId(entry.bid.id)));
      setSelections(function dropDeadSelections(previous) {
        const survivors = Object.entries(previous).filter(([, bidId]) => liveBidIds.has(bidId));
        return survivors.length === Object.keys(previous).length ? previous : Object.fromEntries(survivors);
      });
    },
    [bidsQuery.data]
  );

  /** A closed or leased bid still means a provider bid, so it counts as much as an open one. */
  const hasAnyBid = (bidsQuery.data?.data ?? []).length > 0;

  /** Only a bid list actually read can confirm that nobody bid, so the wait starts once one has loaded. */
  const hasReadBidList = bidsQuery.data !== undefined;

  /** One-shot latch: once any bid appears the no-providers timeout must not re-arm — a later empty list is quote-expiry, not "no providers". */
  const providersEverBidRef = useRef(false);

  const bidsReceivedTrackedRef = useRef(false);

  /** When this attempt's wait for a first bid ran out, so a bid landing later can report how late it came. */
  const noBidsReportedAtRef = useRef<number | null>(null);

  /** Null on a resumed deployment, whose request this session never saw. */
  const screeningRef = useRef<ScreeningSummary | null>(null);

  useEffect(
    function trackFirstBidsReceived() {
      const currentBids = bidsQuery.data?.data;
      if (!currentBids || currentBids.length === 0 || bidsReceivedTrackedRef.current) return;
      bidsReceivedTrackedRef.current = true;
      analyticsService.track("bids_received", {
        category: "deployments",
        numberOfBids: currentBids.length,
        dseq,
        notBiddingProviderCount: screeningRef.current?.notBiddingProviderCount
      });
      if (noBidsReportedAtRef.current === null) return;
      analyticsService.track("bids_received_after_timeout", {
        category: "deployments",
        dseq,
        bidStrategy: bidStrategyRef.current,
        secondsAfterTimeout: Math.round((Date.now() - noBidsReportedAtRef.current) / 1000),
        placementCount: screeningRef.current?.placementCount,
        screenedProviderCount: screeningRef.current?.providerCount,
        notBiddingProviderCount: screeningRef.current?.notBiddingProviderCount,
        isAutoDeploy: isOneClickDeploy(intentRef.current, bidStrategyRef.current)
      });
    },
    [bidsQuery.data, dseq, analyticsService]
  );

  useEffect(
    function reportWhenNoProvidersBid() {
      if (hasAnyBid) {
        providersEverBidRef.current = true;
        setNoBidsReceived(false);
      }
      if (phase !== "quoting" || providersEverBidRef.current || !hasReadBidList) return;
      const timer = setTimeout(function timeOutWithoutProviders() {
        if (discardedRef.current) return;
        analyticsService.track("bids_not_received", {
          category: "deployments",
          dseq,
          bidStrategy: bidStrategyRef.current,
          waitedSeconds: NO_BIDS_TIMEOUT_MS / 1000,
          placementCount: screeningRef.current?.placementCount,
          screenedProviderCount: screeningRef.current?.providerCount,
          notBiddingProviderCount: screeningRef.current?.notBiddingProviderCount
        });
        noBidsReportedAtRef.current = Date.now();
        setNoBidsReceived(true);
      }, NO_BIDS_TIMEOUT_MS);
      return function cancelNoProvidersTimeout() {
        clearTimeout(timer);
      };
    },
    [phase, hasAnyBid, hasReadBidList, dseq, analyticsService]
  );

  /** Everything tied to the deployment that just went away. The caller decides where the flow lands afterwards. */
  const clearDeploymentState = useCallback(function clearDeploymentState() {
    setDseq(null);
    setSelections({});
    setManifest(null);
    setCreatedSdl(null);
    setDeployError(undefined);
    setDeploySucceeded(false);
    setNoBidsReceived(false);
    setAutoLeasedDseq(null);
  }, []);

  /** Leaves `pendingClose` alone, because the close it tracks outlives the reset that hands the form back. */
  const resetToConfiguring = useCallback(
    function resetToConfiguring() {
      clearDeploymentState();
      setError(undefined);
      setPhase("configuring");
    },
    [clearDeploymentState]
  );

  /** Guarded on the settings id because `getBalancesKey(undefined)` is the empty key, which would invalidate every query. */
  const invalidateClosedDeploymentCaches = useCallback(
    function invalidateClosedDeploymentCaches() {
      if (!settingsId) return;
      queryClient.invalidateQueries({ queryKey: QueryKeys.getBalancesKey(settingsId) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.getDeploymentListKey(settingsId) });
      queryClient.invalidateQueries({ queryKey: api.v1.listDeployments.getKey() });
    },
    [queryClient, settingsId, api]
  );

  /** A failure only becomes an error scene when a create was queued behind it, so a purely background one leaves the phase alone. */
  const settleClose = useCallback(
    function settleClose(closedDseq: string, token: number, verifiedClosed: boolean, message?: string) {
      if (token !== closeTokenRef.current) return;
      closingDseqRef.current = null;
      const queuedCreate = queuedCreateRef.current;
      queuedCreateRef.current = null;

      if (verifiedClosed) {
        setPendingClose(null);
        invalidateClosedDeploymentCaches();
        queuedCreate?.();
        return;
      }

      setPendingClose({ dseq: closedDseq, failed: true, message });
      if (!queuedCreate) return;
      setError({ message, kind: "close" });
      setPhase("error");
    },
    [invalidateClosedDeploymentCaches]
  );

  /**
   * tx-signer stops polling (~36s) only after the tx TTL (30s) expires, so a reported failure is often a false negative
   * and one live read is decisive. `token` must not be the create-attempt counter: a re-quote after the cancel would bump
   * that counter and drop the very outcome the queued create waits on.
   */
  const verifyCloseOutcome = useCallback(
    function verifyCloseOutcome(dseqToVerify: string, token: number, cause: unknown) {
      function settle(verifiedClosed: boolean) {
        if (token !== closeTokenRef.current) return;
        analyticsService.track("close_deployment_failed", { category: "deployments", dseq: dseqToVerify, verifiedClosed });
        settleClose(dseqToVerify, token, verifiedClosed, extractApiErrorMessage(cause) ?? undefined);
      }
      getDeployment.mutate(
        { dseq: dseqToVerify },
        {
          onSuccess: function onVerified(result: { data: { deployment: { state: string } } }) {
            settle(result.data.deployment.state === "closed");
          },
          onError: function onVerifyFailed(verifyError: unknown) {
            settle(isApiError(verifyError) && verifyError.status === 404);
          }
        }
      );
    },
    [getDeployment, analyticsService, settleClose]
  );

  /** The only close path, so one mutex and one queue cover every close; one a create waits on is made during the request, so only one deployment is ever open. */
  const startClose = useCallback(
    function startClose(dseqToClose: string, timing: "in-background" | "now" = "now") {
      const token = ++closeTokenRef.current;
      closingDseqRef.current = dseqToClose;
      setBackgroundClose(null);
      setPendingClose({ dseq: dseqToClose, failed: false });
      const inBackground = timing === "in-background";

      function followInBackground(activityId: string) {
        if (token !== closeTokenRef.current) return;
        if (queuedCreateRef.current) {
          startClose(dseqToClose);
          return;
        }
        closingDseqRef.current = null;
        setBackgroundClose({ dseq: dseqToClose, token, activityId });
        queryClient.invalidateQueries({ queryKey: api.v1.listActivities.getKey() });
      }

      closeDeployment.mutate(closeRequestFor(dseqToClose, inBackground), {
        onSuccess: function followOrSettle(result) {
          const activityId = inBackground ? acceptedActivityIdOf(result.data) : undefined;
          if (activityId) followInBackground(activityId);
          else settleClose(dseqToClose, token, true);
        },
        onError: function onCloseFailed(cause: unknown) {
          verifyCloseOutcome(dseqToClose, token, cause);
        }
      });
    },
    [closeDeployment, settleClose, verifyCloseOutcome, queryClient, api]
  );

  useEffect(
    function settleBackgroundCloseFromFeed() {
      if (!backgroundClose) return;
      const activity = activities?.find(({ id }) => id === backgroundClose.activityId);
      if (!activity || activity.status === "pending") return;
      setBackgroundClose(null);
      const verifiedClosed = activity.status === "succeeded";
      if (!verifiedClosed) analyticsService.track("close_deployment_failed", { category: "deployments", dseq: backgroundClose.dseq, verifiedClosed });
      settleClose(backgroundClose.dseq, backgroundClose.token, verifiedClosed, activity.meta.error?.message);
    },
    [activities, backgroundClose, settleClose, analyticsService]
  );

  const runQueuedCreate = useCallback(function runQueuedCreate() {
    const queuedCreate = queuedCreateRef.current;
    queuedCreateRef.current = null;
    queuedCreate?.();
  }, []);

  /** A create from a superseded attempt still opened a deployment, which is closed at once when a newer create waits on it. */
  const closeSupersededCreate = useCallback(
    function closeSupersededCreate(openedDseq: string) {
      if (discardedRef.current) return;
      startClose(openedDseq, queuedCreateRef.current ? "now" : "in-background");
    },
    [startClose]
  );

  useEffect(
    function settleBackgroundCreate() {
      const activity = backgroundCreateActivity?.data;
      if (!backgroundCreate || !activity || activity.status === "pending") return;
      setBackgroundCreate(null);
      createInFlightRef.current = false;
      const isCreated = activity.status === "succeeded";

      if (backgroundCreate.attempt !== createAttemptRef.current) {
        if (isCreated) closeSupersededCreate(backgroundCreate.dseq);
        else runQueuedCreate();
        return;
      }

      if (isCreated) {
        setPhase("quoting");
        return;
      }

      router.replace(buildConfigureUrl(intentRef.current, undefined, bidStrategyRef.current), undefined, { shallow: true });
      clearDeploymentState();
      analyticsService.track("create_deployment_failed", { category: "deployments", kind: "create", inBackground: true });
      setError({ message: activity.meta.error?.message, kind: "create" });
      setPhase("error");
    },
    [backgroundCreateActivity, backgroundCreate, closeSupersededCreate, runQueuedCreate, clearDeploymentState, router, analyticsService]
  );

  /** A deployment this session opened that is known to be still open with no close in flight: the next create closes it first. */
  const strandedDseq = pendingClose?.failed ? pendingClose.dseq : null;

  /** Seals against the console's current key; a create binds the seal to its SDL, a patch binds to none because the api checks it against the stored document. */
  const sealSecrets = useCallback(
    async function sealSecrets(secrets: SdlSecretValues, sdl?: string): Promise<string> {
      const context = await getSdlSecretsContext.mutateAsync();
      return await dependencies.sealSdlSecrets({ context: context.data, secrets, ...(sdl === undefined ? {} : { sdl }) });
    },
    [getSdlSecretsContext, dependencies]
  );

  /**
   * A still-open deployment is closed first, and a create fired mid-close or while an abandoned create is still out waits
   * on it rather than racing it, so only one deployment is ever open. The typed values are sealed to the console's
   * current key first, and a seal the api reports stale is remade once against a fresh key.
   */
  const requestQuotes = useCallback(
    function requestQuotes(sdl: string, options: RequestQuotesOptions = {}) {
      lastQuoteRequestRef.current = { sdl, options };
      const attempt = ++createAttemptRef.current;
      providersEverBidRef.current = false;
      bidsReceivedTrackedRef.current = false;
      noBidsReportedAtRef.current = null;
      screeningRef.current = options.screening ?? null;
      setError(undefined);
      setNoBidsReceived(false);
      const secrets = options.secrets ?? {};

      function isCurrentAttempt() {
        return attempt === createAttemptRef.current;
      }

      function adoptDeployment({ dseq: newDseq, manifest }: { dseq: string; manifest: string }) {
        setDseq(newDseq);
        setManifest(manifest);
        setCreatedSdl(sdl);
        setSelections({});
        setDeployError(undefined);
        setDeploySucceeded(false);
        router.replace(buildConfigureUrl(intentRef.current, newDseq, bidStrategyRef.current), undefined, { shallow: true });
      }

      function createdEventOf(newDseq: string) {
        return {
          category: "deployments" as const,
          label: "Create deployment in wizard",
          dseq: newDseq,
          secretCount: Object.keys(secrets).length,
          notBiddingProviderCount: options.screening?.notBiddingProviderCount
        };
      }

      /** Only a create still current takes the page over and holds its analytics until the feed reports it created. */
      function followInBackground(result: { dseq: string; manifest: string }, activityId: string) {
        setBackgroundCreate({ dseq: result.dseq, activityId, attempt });
        followUntilSettled(activityId);
        queryClient.invalidateQueries({ queryKey: api.v1.listActivities.getKey() });
        if (!isCurrentAttempt()) return;
        adoptDeployment(result);
        holdActivityEvents(activityId, { succeeded: [{ event: "create_deployment", properties: createdEventOf(result.dseq) }] });
      }

      /** Never adopted, so no close path here can reach it, even once superseded; the URL carries it so a reload finds it rather than creating another. */
      function handOverToApi(newDseq: string, activityId: string) {
        createInFlightRef.current = false;
        recordOneClickDeploy(newDseq, intentRef.current.templateId);
        holdActivityEvents(activityId, {
          succeeded: [{ event: "create_deployment", properties: createdEventOf(newDseq) }],
          failed: [{ event: "create_deployment_failed", properties: { category: "deployments", kind: "create", inBackground: true } }]
        });
        queryClient.invalidateQueries({ queryKey: api.v1.listActivities.getKey() });
        if (!isCurrentAttempt()) {
          runQueuedCreate();
          return;
        }
        setAutoLeasedDseq(newDseq);
        router.replace(buildConfigureUrl(intentRef.current, newDseq, bidStrategyRef.current), undefined, { shallow: true });
      }

      /** The api answers `autoLease: true` only once it has taken the provider pick and the lease on as well, and ignores the ask while that is unavailable. */
      function onCreated(result: { data: { dseq: string; manifest: string; autoLease?: boolean } }) {
        const activityId = acceptedActivityIdOf(result.data);
        if (activityId && result.data.autoLease) {
          handOverToApi(result.data.dseq, activityId);
          return;
        }
        if (activityId) {
          followInBackground(result.data, activityId);
          return;
        }
        createInFlightRef.current = false;
        markOutcomeHandled("deployment_create", result.data.dseq, "succeeded");
        if (!isCurrentAttempt()) {
          closeSupersededCreate(result.data.dseq);
          return;
        }
        adoptDeployment(result.data);
        setPhase("quoting");
        analyticsService.track("create_deployment", createdEventOf(result.data.dseq));
      }

      function failCreate(flowError: FlowError, cause: unknown) {
        analyticsService.track("create_deployment_failed", createFailedEventOf(flowError, cause));
        setError(flowError);
        setPhase("error");
      }

      function onCreateFailed(cause: unknown) {
        if (!isCurrentAttempt()) return;
        failCreate(describeCreateFailure(cause, deployTargetProjectId), cause);
      }

      const inheritance = options.inheritSecretsFrom ? { inheritSecretsFrom: options.inheritSecretsFrom } : {};

      function submitCreate(sealed: { sealedSecrets?: string }, canResealOnce: boolean) {
        createInFlightRef.current = true;
        createDeployment.mutate(
          {
            data: {
              sdl,
              ...namePayload(options.name),
              ...projectPayload(deployTargetProjectId),
              ...templatePayload(intentRef.current.templateId),
              ...sealed,
              ...inheritance,
              deposit: DEFAULT_DEPOSIT
            },
            ...createQueryFor(isOneClickDeploy(intentRef.current, bidStrategyRef.current))
          },
          {
            onSuccess: onCreated,
            onError: function retryOrFail(cause: unknown) {
              createInFlightRef.current = false;
              if (!isCurrentAttempt()) {
                runQueuedCreate();
                return;
              }
              if (isInheritedSecretsUnreadable(cause)) {
                failCreate({ message: extractApiErrorMessage(cause) ?? undefined, kind: "inherited-unreadable" }, cause);
                return;
              }
              if (canResealOnce && isStaleSealingKey(cause)) {
                void sealAndSubmit(false);
                return;
              }
              const projectRefusal = describeProjectRefusal(cause);
              if (projectRefusal) forgetProject(projectRefusal);
              onCreateFailed(cause);
            }
          }
        );
      }

      async function sealAndSubmit(canResealOnce: boolean) {
        let sealedSecrets: string;
        try {
          sealedSecrets = await sealSecrets(secrets, sdl);
        } catch (cause) {
          onCreateFailed(cause);
          return;
        }
        if (!isCurrentAttempt()) return;
        submitCreate({ sealedSecrets }, canResealOnce);
      }

      function create() {
        if (!isCurrentAttempt()) return;
        setPhase("creating");
        void sealAndSubmit(true);
      }

      const openDseq = createdDseq ?? strandedDseq ?? backgroundClose?.dseq;
      if (openDseq) {
        setPhase("creating");
        setDseq(null);
        queuedCreateRef.current = create;
        startClose(openDseq);
        return;
      }

      if (closingDseqRef.current || createInFlightRef.current) {
        setPhase("creating");
        setDseq(null);
        queuedCreateRef.current = create;
        return;
      }

      create();
    },
    [
      createDeployment,
      sealSecrets,
      createdDseq,
      strandedDseq,
      backgroundClose,
      router,
      analyticsService,
      startClose,
      closeSupersededCreate,
      runQueuedCreate,
      holdActivityEvents,
      recordOneClickDeploy,
      followUntilSettled,
      queryClient,
      api,
      deployTargetProjectId,
      forgetProject,
      markOutcomeHandled
    ]
  );

  const requestQuotesAgain = useCallback(
    function requestQuotesAgain() {
      const lastRequest = lastQuoteRequestRef.current;
      if (lastRequest) requestQuotes(lastRequest.sdl, lastRequest.options);
    },
    [requestQuotes]
  );

  /**
   * The dseq leaves the URL on the same tick, so a returning user never sees the abandoned deployment. Falls back to
   * `strandedDseq` rather than any pending dseq so a second press cannot broadcast a duplicate close for one settling.
   */
  const cancelAndEdit = useCallback(
    function cancelAndEdit() {
      router.replace(buildConfigureUrl(intentRef.current, undefined, bidStrategy), undefined, { shallow: true });
      createAttemptRef.current += 1;
      const isCreateOutstanding = phase === "creating" && !queuedCreateRef.current && !autoLeasedDseq;
      queuedCreateRef.current = null;
      const dseqToClose = createdDseq ?? strandedDseq;
      if (!dseqToClose && isCreateOutstanding) analyticsService.track("cancel_during_create", { category: "deployments" });
      resetToConfiguring();
      if (dseqToClose) startClose(dseqToClose, "in-background");
    },
    [createdDseq, strandedDseq, phase, autoLeasedDseq, router, bidStrategy, analyticsService, resetToConfiguring, startClose]
  );
  /**
   * Lands in `error`, never `configuring`: the auto flow creates a fresh deployment the instant it reads `configuring`.
   * Clearing the dseq up front is what stops a retry in the close's window from broadcasting a second one, since every
   * other close path falls back to `strandedDseq`, which only names a deployment whose close already came back open.
   */
  const closeAndFail = useCallback(
    function closeAndFail(message: string, kind: AbandonedAttemptKind = "no-match") {
      createAttemptRef.current += 1;
      queuedCreateRef.current = null;
      router.replace(buildConfigureUrl(intentRef.current, undefined, bidStrategyRef.current), undefined, { shallow: true });
      const dseqToClose = createdDseq ?? strandedDseq;
      clearDeploymentState();
      setError({ message, kind });
      setPhase("error");
      if (dseqToClose) startClose(dseqToClose, "in-background");
    },
    [createdDseq, strandedDseq, router, clearDeploymentState, startClose]
  );

  const retryClose = useCallback(
    function retryClose() {
      if (!strandedDseq) return;
      startClose(strandedDseq, "in-background");
    },
    [strandedDseq, startClose]
  );

  const discard = useCallback(
    function discard() {
      if (discardedRef.current || isLeaseInFlight) return;
      discardedRef.current = true;
      createAttemptRef.current += 1;
      queuedCreateRef.current = null;
      const dseqToClose = createdDseq ?? strandedDseq;
      if (dseqToClose && closingDseqRef.current !== dseqToClose) startClose(dseqToClose, "in-background");
    },
    [createdDseq, strandedDseq, startClose, isLeaseInFlight]
  );

  const setBidStrategy = useCallback(
    function setBidStrategy(strategy: BidStrategy, nextRecommendedBidIds: string[] = []) {
      setBidStrategyState(strategy);
      setRecommendedBidIds(nextRecommendedBidIds);
      router.replace(buildConfigureUrl(intentRef.current, dseq ?? undefined, strategy), undefined, { shallow: true });
    },
    [router, dseq]
  );

  const refreshQuotes = useCallback(function refreshQuotes() {
    setPhase("quoting");
  }, []);

  const retry = useCallback(
    function retry() {
      setError(undefined);
      setPhase(dseq ? "quoting" : "configuring");
    },
    [dseq]
  );

  const selectProvider = useCallback(
    function selectProvider(placementId: string, bidId: string) {
      setDeployError(undefined);
      setSelections(previous => ({ ...previous, [placementId]: bidId }));
      analyticsService.track("bid_selected", "Amplitude");
    },
    [analyticsService]
  );

  const clearSelection = useCallback(function clearSelection(placementId: string) {
    setSelections(function omitPlacement(previous) {
      const next = { ...previous };
      delete next[placementId];
      return next;
    });
  }, []);

  /**
   * The manifest is derived from the SDL being deployed (not the create-time one) so a quoting-window edit gets leased;
   * when it differs from create the deployment is updated first so the on-chain hash matches before the manifest is sent.
   * That update is a patch of what changed since the create, because the whole-SDL update seals every value and cannot
   * resolve a reference.
   */
  const deploy = useCallback(
    function deploy(sdl: string, options: DeployOptions = {}) {
      if (discardedRef.current) return;
      const attempt: DeployAttempt = { dseq, isAutoDeploy: !!options.isAutoDeploy };

      function trackDeployFailed(stage: DeployFailureStage, cause?: unknown) {
        const { event, properties } = deployFailedEventOf(stage, attempt, cause);
        analyticsService.track(event, properties);
      }

      function refuseDeploy(stage: DeployFailureStage, message: string) {
        trackDeployFailed(stage);
        setDeployError({ message });
      }

      if (!dseq) {
        trackDeployFailed("not_started");
        return;
      }
      const activeDseq = dseq;
      const nextManifest = dependencies.manifestFromSdl(sdl);
      const leases = Object.values(selections).map(parseBidId);
      if (!nextManifest) {
        refuseDeploy("manifest_build", MANIFEST_BUILD_MESSAGE);
        return;
      }
      if (leases.length === 0) {
        refuseDeploy("no_selection", NO_SELECTION_MESSAGE);
        return;
      }
      /** A kept reference may already be sealed against this deployment, which only the api can confirm; a name this form minted is held by nothing. */
      const mintedWithoutValue = (options.unresolvedSecrets ?? []).filter(secret => !secret.isKeptReference);
      if (mintedWithoutValue.length > 0) {
        refuseDeploy("unresolved_secret", mintedWithoutValue.map(unresolvedSecretMessage).join(" "));
        return;
      }
      const activeManifest = nextManifest;
      /** A corrected secret leaves the SDL untouched, because its reference is what the SDL carries, so an unchanged manifest still has to patch. */
      const hasTypedSecrets = Object.keys(options.secrets ?? {}).length > 0;
      const keepsStoredReference = hasSdlReference(sdl);
      const deployEvents = deployEventsOf(activeDseq, dependencies.deploymentResourcesFromSdl(sdl));
      const attemptNumber = ++deployAttemptRef.current;
      const isReplaced = () => attemptNumber !== deployAttemptRef.current;
      setDeployError(undefined);
      setDeploySucceeded(false);
      setShowsDeployProgress(false);
      setPhase("deploying");
      setWatchedDeploy(options.isAutoDeploy ? null : { attempt, hasTimedOut: false });

      /**
       * The onboarding gate reads "onboarded" off the leases cache, empty until this first deploy, so refreshing it
       * keeps a client-side nav to /deployments from bouncing the user back to onboarding until a full reload.
       */
      function completeDeploy(owner: string) {
        for (const { event, properties } of deployEvents) {
          analyticsService.track(event, properties);
        }
        queryClient.invalidateQueries({ queryKey: QueryKeys.getLeaseExistenceKey(owner) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.getAllLeasesKey(owner) });
        queryClient.invalidateQueries({ queryKey: api.v1.getSpendRate.getKey() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.getDeploymentListKey(owner) });
        queryClient.invalidateQueries({ queryKey: api.v1.listDeployments.getKey() });
        setDeploySucceeded(true);
        redirectTimerRef.current = setTimeout(function redirectToDeployment() {
          router.replace(UrlService.deploymentDetails(activeDseq, "EVENTS", "events"));
        }, DEPLOY_SUCCESS_DWELL_MS);
      }

      /** The lease does not exist yet, so the onboarding gate is told to expect it and the list waits for the feed that shows it spinning up. */
      async function followDeployInBackground(activityId: string) {
        holdActivityEvents(activityId, { succeeded: deployEvents, failed: [deployFailedEventOf("background_lease", attempt)] });
        if (settingsId) {
          queryClient.setQueryData(QueryKeys.getLeaseExistenceKey(settingsId), true);
          queryClient.invalidateQueries({ queryKey: QueryKeys.getDeploymentListKey(settingsId) });
        }
        queryClient.invalidateQueries({ queryKey: api.v1.listDeployments.getKey() });
        setDeploySucceeded(true);
        setIsDeployedInBackground(true);
        await queryClient.invalidateQueries({ queryKey: api.v1.listActivities.getKey() });
        router.replace(UrlService.deploymentList());
      }

      function failDeploy(stage: DeployFailureStage, cause: unknown) {
        if (isReplaced()) return;
        trackDeployFailed(stage, cause);
        setDeployError({
          message: extractApiErrorMessage(cause) ?? undefined,
          canLeaseElsewhere: stage === "lease_create" && canLeaseElsewhere(cause)
        });
        setPhase("quoting");
      }

      /**
       * A manifest hashed from references would ship them to the provider as literal values, so the api's own derivation is
       * left as the only source. A lease the api took on in the background is not marked, as the activity host tells how it ends.
       */
      function sendManifestAndLease() {
        const lease = keepsStoredReference ? { leases } : { manifest: activeManifest, leases };
        forgetEarlierOutcomes("lease_create", activeDseq);
        setIsAwaitingLeaseHandoff(true);
        setIsLeaseInFlight(true);
        createLease.mutate(
          { ...lease, ...BACKGROUND_QUERY },
          {
            onSuccess: function completeOrFollowDeploy(result) {
              setIsLeaseInFlight(false);
              if (isReplaced()) return;
              setIsAwaitingLeaseHandoff(false);
              const activityId = acceptedActivityIdOf(result.data);
              if (activityId) {
                void followDeployInBackground(activityId);
                return;
              }
              markOutcomeHandled("lease_create", activeDseq, "succeeded");
              completeDeploy(result.data.deployment.id.owner);
            },
            onError: function markAndFailDeploy(cause: unknown) {
              setIsLeaseInFlight(false);
              if (isReplaced()) return;
              setIsAwaitingLeaseHandoff(false);
              markOutcomeHandled("lease_create", activeDseq, "failed");
              failDeploy("lease_create", cause);
            }
          }
        );
      }

      function updateWholeSdlAndLease() {
        updateDeployment.mutate({ dseq: activeDseq, data: { sdl } }, { onSuccess: sendManifestAndLease, onError: cause => failDeploy("update", cause) });
      }

      function submitPatch(services: ServicesPatch | undefined, sealedSecrets: string, canResealOnce: boolean) {
        forgetEarlierOutcomes("deployment_update", activeDseq);
        patchDeployment.mutate(
          { dseq: activeDseq, data: { ...(services === undefined ? {} : { services }), sealedSecrets } },
          {
            onSuccess: sendManifestAndLease,
            onError: function resealOrFail(cause: unknown) {
              if (canResealOnce && isStaleSealingKey(cause)) {
                void sealAndPatch(services, false);
                return;
              }
              failDeploy("patch", cause);
            },
            onSettled: (_result, error) => markOutcomeHandled("deployment_update", activeDseq, shownOutcomeOf(error))
          }
        );
      }

      async function sealAndPatch(services: ServicesPatch | undefined, canResealOnce: boolean) {
        try {
          submitPatch(services, await sealSecrets(options.secrets ?? {}), canResealOnce);
        } catch (cause) {
          failDeploy("seal", cause);
        }
      }

      /** A session that never saw the create diffs against the stored definition; with none to diff against, the whole SDL goes out as before. */
      async function patchChangesAndLease() {
        let services: ServicesPatch | undefined;
        try {
          const baselineSdl = createdSdl ?? (await storedSdlOf(activeDseq));
          if (baselineSdl === null) {
            updateWholeSdlAndLease();
            return;
          }
          const patched = dependencies.servicesPatchBetween(baselineSdl, sdl);
          services = isEmptyServicesPatch(patched) ? undefined : patched;
        } catch (cause) {
          failDeploy("patch", cause);
          return;
        }

        if (services === undefined && !hasTypedSecrets) {
          sendManifestAndLease();
        } else {
          await sealAndPatch(services, true);
        }
      }

      async function storedSdlOf(dseqToRead: string): Promise<string | null> {
        const result = await getDeployment.mutateAsync({ dseq: dseqToRead });
        return result.data.consoleSettings?.sdl ?? null;
      }

      if (activeManifest === manifest && !hasTypedSecrets) {
        sendManifestAndLease();
      } else {
        void patchChangesAndLease();
      }
    },
    [
      createLease,
      updateDeployment,
      patchDeployment,
      getDeployment,
      sealSecrets,
      createdSdl,
      dseq,
      manifest,
      selections,
      router,
      dependencies,
      queryClient,
      analyticsService,
      forgetEarlierOutcomes,
      markOutcomeHandled,
      api,
      settingsId,
      holdActivityEvents
    ]
  );

  return {
    phase,
    dseq,
    bidStrategy,
    selections,
    bids: bidsQuery.data?.data ?? [],
    deploySucceeded,
    isDeployedInBackground,
    showsDeployProgress,
    isDeployTakingLong: !!watchedDeploy?.hasTimedOut && phase === "deploying" && !deploySucceeded,
    noBidsReceived,
    deployError,
    error,
    pendingClose,
    isCreatingInBackground: backgroundCreate?.dseq === dseq || autoLeasedDseq !== null,
    isLeaseInFlight,
    autoLeasedDseq,
    recommendedBidIds,
    actions: {
      requestQuotes,
      requestQuotesAgain,
      cancelAndEdit,
      closeAndFail,
      retryClose,
      discard,
      setBidStrategy,
      refreshQuotes,
      retry,
      selectProvider,
      clearSelection,
      deploy
    }
  };
}

function closeRequestFor(dseq: string, inBackground: boolean): { dseq: string; async?: "true" } {
  return inBackground ? { dseq, ...BACKGROUND_QUERY } : { dseq };
}

function createQueryFor(isOneClick: boolean): { async: "true"; autoLease?: "true" } {
  return isOneClick ? { ...BACKGROUND_QUERY, autoLease: "true" } : BACKGROUND_QUERY;
}

function isOneClickDeploy(intent: DeploymentIntent, bidStrategy: BidStrategy): boolean {
  return intent.sdlStrategy === "default" && bidStrategy === "auto";
}

function shownOutcomeOf(error: unknown): "succeeded" | "failed" {
  return error ? "failed" : "succeeded";
}

/** The activity the api answered a background close, create or lease with; one it finished during the request answers without one. */
function acceptedActivityIdOf(data: object): string | undefined {
  return "activityId" in data && typeof data.activityId === "string" ? data.activityId : undefined;
}

/** The api refuses a blank name rather than reading it as "unnamed", so a name the user left empty is left out of the request entirely. */
function namePayload(name: string | undefined): { name?: string } {
  const trimmed = name?.trim();
  return trimmed ? { name: trimmed } : {};
}

/** Hardcoded starters such as hello-world are not in the gallery, so they have no guide to link the deployment to. */
function templatePayload(templateId: string | undefined): { templateId?: string } {
  return templateId && !hardcodedTemplates.some(template => template.code === templateId) ? { templateId } : {};
}

/** Why the api refused the project the deployment was sent to, which dropping the choice turns into a fresh pick. */
function describeProjectRefusal(cause: unknown): string | undefined {
  if (!isApiError(cause)) return undefined;
  if (extractApiErrorCode(cause) === PROJECT_REQUIRED_ERROR_CODE) return "Choose the project this deployment goes to.";
  if (cause.status === HTTP_NOT_FOUND && extractApiErrorMessage(cause) === PROJECT_NOT_FOUND_MESSAGE) {
    return "The project you picked was deleted or you lost access to it. Choose another one.";
  }
  return undefined;
}

/** Without a project the api files the deployment into the organization's default project. */
function projectPayload(projectId: string | undefined): { projectId?: string } {
  return projectId ? { projectId } : {};
}

/** Matched on status because an exhausted fee allowance and a trial-blocked GPU still report the generic `payment_required`, and adding funds clears them too. */
function isPaymentRequired(cause: unknown): boolean {
  return isApiError(cause) && cause.status === HTTP_PAYMENT_REQUIRED;
}

function describeCreateFailure(cause: unknown, deployTargetProjectId: string | undefined): FlowError {
  if (extractApiErrorCode(cause) === WALLET_PROVISIONING_ERROR_CODE) return { message: WALLET_PROVISIONING_TIMEOUT_MESSAGE, kind: "create" };

  const message = extractApiErrorMessage(cause) ?? undefined;
  const budgetRefusal = readProjectBudgetRefusal(cause);
  if (budgetRefusal) return { message, kind: "budget-exceeded", projectId: budgetRefusal.projectId ?? deployTargetProjectId };
  if (isBalanceTopUpPending(cause)) return { message, kind: "top-up-pending" };
  if (isPaymentRequired(cause)) return { message, kind: "needs-funds", shortfall: extractDepositShortfall(cause) };
  return { message, kind: "create" };
}

/** Carries the status rather than the api's message, which can quote names from the user's spec. */
function createFailedEventOf(flowError: FlowError, cause: unknown) {
  return { category: "deployments" as const, kind: flowError.kind, ...(isApiError(cause) ? { status: cause.status } : {}) };
}

/** A seal made against a retired key comes back as a bare 409; a wallet still provisioning answers 409 too and has its own retry. */
function isStaleSealingKey(cause: unknown): boolean {
  return isApiError(cause) && cause.status === HTTP_CONFLICT && !isWalletProvisioning(cause) && !isInheritedSecretsUnreadable(cause);
}

function isInheritedSecretsUnreadable(cause: unknown): boolean {
  return extractApiErrorCode(cause) === INHERITED_SECRETS_UNREADABLE_CODE;
}

/** The provider manifest for an SDL, or null when it can't be built (invalid/mid-edit). Matches the server's create-deployment manifest, so the update-before-lease comparison in `deploy` holds. */
function manifestFromSdl(sdl: string): string | null {
  try {
    return ManifestYaml(sdl);
  } catch {
    return null;
  }
}

function deployEventsOf(dseq: string, resources: ReturnType<typeof deploymentResourcesFromSdl>): HeldActivityEvent[] {
  const leaseProperties = { category: "deployments" as const, label: "Create lease", dseq, ...resources };

  return [
    { event: "create_lease", properties: leaseProperties },
    ...(resources.gpuAmount > 0 ? [{ event: "create_gpu_deployment" as const, properties: leaseProperties }] : []),
    { event: "send_manifest", properties: { category: "deployments", label: "Send manifest after creating lease", dseq } }
  ];
}

type DeployFailureStage =
  | "not_started"
  | "manifest_build"
  | "no_selection"
  | "unresolved_secret"
  | "seal"
  | "patch"
  | "update"
  | "lease_create"
  | "background_lease"
  | "lease_timeout";

type DeployAttempt = { dseq: string | null; isAutoDeploy: boolean };

/** Leaves the api's error message out, since it can echo what the user typed. */
function deployFailedEventOf(stage: DeployFailureStage, attempt: DeployAttempt, cause?: unknown): HeldActivityEvent {
  return {
    event: "deploy_failed",
    properties: {
      category: "deployments",
      stage,
      ...attempt,
      inBackground: true,
      errorCode: extractApiErrorCode(cause) ?? undefined,
      errorStatus: isApiError(cause) ? cause.status : undefined,
      errorKind: cause === undefined ? undefined : deployErrorKindOf(cause)
    }
  };
}

function deploymentResourcesFromSdl(sdl: string): { gpuAmount: number; cpuAmount: number; memoryAmount: number; storageAmount: number } {
  try {
    const totals = aggregateDeploymentResources(importSimpleSdl(sdl).services);
    return { gpuAmount: totals.gpu, cpuAmount: totals.cpu, memoryAmount: totals.memoryBytes, storageAmount: totals.ephemeralBytes + totals.persistentBytes };
  } catch {
    return { gpuAmount: 0, cpuAmount: 0, memoryAmount: 0, storageAmount: 0 };
  }
}

/** Builds the canonical configure URL preserving both template ids, sdl-strategy, draftId and vm alongside the current dseq + bid-strategy. */
export function buildConfigureUrl(intent: DeploymentIntent, dseq: string | undefined, bidStrategy: BidStrategy): string {
  return UrlService.configureDeployment({
    dseq,
    templateId: intent.templateId,
    userTemplateId: intent.userTemplateId,
    sdlStrategy: intent.templateId ? intent.sdlStrategy : undefined,
    bidStrategy,
    draftId: intent.draftId,
    vm: intent.vm,
    exampleImage: intent.exampleImage
  });
}
